import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/axiosInstance';

// Import Icons
import {
  GlobeAltIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChevronRightIcon,
  CloudIcon,
  ShieldCheckIcon,
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import logger from '../../../utils/logger';

// Provider logos/icons mapping - can be replaced with actual SVG icons later
const ProviderIcon = ({ provider, className = "h-6 w-6" }) => {
  const iconMap = {
    cloudflare: CloudIcon,
    route53: CloudIcon,
    godaddy: GlobeAltIcon,
    namecheap: GlobeAltIcon,
    google: CloudIcon,
    digitalocean: CloudIcon,
    linode: CloudIcon,
    netlify: CloudIcon,
    unknown: GlobeAltIcon
  };

  const IconComponent = iconMap[provider] || GlobeAltIcon;
  return <IconComponent className={className} />;
};

const DnsProviderDetection = ({ domain, onProviderDetected, onAutomationClick, showNotification }) => {
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [providerInfo, setProviderInfo] = useState(null);
  const [error, setError] = useState(null);
  const [oauthInitiating, setOauthInitiating] = useState(false);

  // Token wizard state
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [tokenStep, setTokenStep] = useState(1);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [createdRecords, setCreatedRecords] = useState([]);
  const [failedRecords, setFailedRecords] = useState([]);

  // Load cached provider info on mount
  useEffect(() => {
    if (domain?.dnsProvider && domain.dnsProvider.provider !== 'unknown') {
      setProviderInfo({
        provider: domain.dnsProvider.provider,
        name: domain.dnsProvider.name,
        nameservers: domain.dnsProvider.nameservers,
        apiSupported: domain.dnsProvider.apiSupported,
        oauthSupported: domain.dnsProvider.oauthSupported,
        confidence: domain.dnsProvider.confidence,
        lastCheck: domain.dnsProvider.lastCheck,
        connectedAt: domain.dnsProvider.connectedAt,
        canAutomate: domain.dnsProvider.apiSupported
      });
    } else {
      // Auto-detect on first load
      handleDetectProvider();
    }
  }, [domain]);

  const handleDetectProvider = async (isManualRefresh = false) => {
    if (!domain || !domain.id) {
      setError('Domain information is missing');
      return;
    }

    try {
      setDetecting(true);
      setError(null);

      const response = await axiosInstance.get(`/api/business/domains/${domain.id}/dns-provider`);

      if (response.data.success && response.data.provider) {
        setProviderInfo(response.data.provider);

        if (onProviderDetected) {
          onProviderDetected(response.data.provider);
        }

        if (isManualRefresh) {
          if (response.data.provider.provider !== 'unknown') {
            showNotification?.(`DNS provider detected: ${response.data.provider.name}`, 'success');
          } else {
            showNotification?.('Could not identify DNS provider automatically', 'info');
          }
        }
      } else {
        throw new Error(response.data.message || 'Provider detection failed');
      }
    } catch (err) {
      logger.error('Error detecting DNS provider:', err);
      setError(err.message || 'Failed to detect DNS provider');
      if (isManualRefresh) {
        showNotification?.('Failed to detect DNS provider', 'error');
      }
    } finally {
      setDetecting(false);
    }
  };

  const handleAutomateDNS = async () => {
    if (!providerInfo || !providerInfo.canAutomate) return;

    if (providerInfo.provider === 'cloudflare') {
      // Primary path: send the user to Cloudflare login (OAuth) so they
      // authorize DNS access for their own account — no token to copy.
      // The token-paste modal remains available as a fallback link.
      connectCloudflareOAuth();
    } else {
      if (onAutomationClick) {
        onAutomationClick(providerInfo);
      }
    }
  };

  // Open the API-token wizard modal (fallback for users who prefer a token
  // or whose OAuth authorization didn't work).
  const openTokenModal = () => {
    setShowTokenModal(true);
    setTokenStep(1);
    setTokenInput('');
    setValidationError(null);
    setCreatedRecords([]);
    setFailedRecords([]);
  };

  const handleCloseModal = () => {
    setShowTokenModal(false);
    setTokenInput('');
    setTokenStep(1);
    setValidationError(null);
    setOauthInitiating(false);
  };

  const processCloudflareToken = async () => {
    if (!tokenInput.trim()) {
      setValidationError('Please paste your API token');
      return;
    }

    try {
      setValidating(true);
      setValidationError(null);

      const connectResponse = await axiosInstance.post(
        `/api/business/domains/${domain.id}/oauth/cloudflare`,
        { apiToken: tokenInput.trim() }
      );

      if (!connectResponse.data.success) {
        throw new Error(connectResponse.data.message || 'Failed to connect Cloudflare');
      }

      // Success — move to step 3
      const created = connectResponse.data.recordsCreated || [];
      const existed = connectResponse.data.recordsExisted || [];
      const failed = connectResponse.data.recordsFailed || [];
      const successCount = created.length + existed.length;
      setCreatedRecords([...created, ...existed]);
      setFailedRecords(failed);
      setTokenStep(3);

      // Update provider info to show connected state
      const updatedProvider = {
        ...providerInfo,
        connectedAt: new Date().toISOString()
      };
      setProviderInfo(updatedProvider);
      if (onProviderDetected) {
        onProviderDetected(updatedProvider);
      }

      const parts = [];
      if (created.length > 0) parts.push(`${created.length} created`);
      if (existed.length > 0) parts.push(`${existed.length} already existed`);
      showNotification?.(`Cloudflare connected — ${parts.join(', ')}`, 'success');

      // Auto-trigger domain verification after a short delay
      setTimeout(async () => {
        try {
          const verifyResponse = await axiosInstance.post(`/api/business/domains/${domain.id}/verify`);
          if (verifyResponse.data.success && verifyResponse.data.domain?.isVerified) {
            showNotification?.('Domain verified successfully!', 'success');
          }
        } catch (verifyErr) {
          // Verification will happen later via propagation
        }
      }, 2000);

    } catch (err) {
      logger.error('Error processing Cloudflare token:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to connect Cloudflare';
      setValidationError(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  // Initiate the Cloudflare OAuth redirect flow (no API token). Calls the
  // tokenless initiate endpoint, then sends the browser to Cloudflare's
  // authorization page. Cloudflare redirects back to /oauth/cloudflare/callback,
  // handled by CloudflareOAuthCallback.
  const connectCloudflareOAuth = async () => {
    try {
      setOauthInitiating(true);
      setValidationError(null);
      const res = await axiosInstance.post(
        `/api/business/domains/${domain.id}/oauth/cloudflare`,
        {} // no apiToken → OAuth redirect flow
      );
      if (res.data?.success && res.data.authUrl) {
        window.location.href = res.data.authUrl;
      } else {
        throw new Error(res.data?.message || 'Failed to start Cloudflare authorization');
      }
    } catch (err) {
      logger.error('Error initiating Cloudflare OAuth:', err);
      setValidationError(err.response?.data?.message || err.message || 'Failed to start Cloudflare authorization');
      setOauthInitiating(false);
    }
  };

  const getProviderColor = (provider) => {
    const colors = {
      cloudflare: 'text-orange-600 border-orange-200 bg-orange-50 dark:bg-orange-900/20',
      route53: 'text-yellow-600 border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20',
      godaddy: 'text-cyan-600 border-cyan-200 bg-cyan-50 dark:bg-cyan-900/20',
      namecheap: 'text-red-600 border-red-200 bg-red-50 dark:bg-red-900/20',
      google: 'text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-900/20',
      digitalocean: 'text-blue-500 border-blue-200 bg-blue-50 dark:bg-blue-900/20',
      linode: 'text-green-600 border-green-200 bg-green-50 dark:bg-green-900/20',
      netlify: 'text-teal-600 border-teal-200 bg-teal-50 dark:bg-teal-900/20',
      unknown: 'text-gray-600 border-gray-200 bg-gray-50 dark:bg-[#111111]/20'
    };

    return colors[provider] || colors.unknown;
  };

  const isConnected = providerInfo?.connectedAt;
  // Once the domain is verified the DNS records are already in place, so
  // offering to add them ("Verify with Cloudflare" / "Add Automatically") is
  // both redundant and confusing next to a green Verified badge.
  const canShowAutomation = providerInfo?.canAutomate && !isConnected && !domain?.isVerified;

  const [showRecords, setShowRecords] = useState(false);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const handleViewRecords = async () => {
    if (!isConnected || !domain?.id) return;

    try {
      setLoadingRecords(true);
      const response = await axiosInstance.get(`/api/business/domains/${domain.id}/dns-records`);
      if (response.data.success) {
        setDnsRecords(response.data.records || []);
        setShowRecords(true);
      }
    } catch (err) {
      logger.error('Error fetching DNS records:', err);
      showNotification?.('Failed to load DNS records', 'error');
    } finally {
      setLoadingRecords(false);
    }
  };

  // Cloudflare token creation page — use "Edit zone DNS" template
  const cloudflareTokenUrl = 'https://dash.cloudflare.com/profile/api-tokens';

  if (loading) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full"></div>
        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading DNS provider info...</span>
      </div>
    );
  }

  return (
    <div className="border-b border-gray-200 dark:border-gray-800">
      {/* Header */}
      <div className="p-4 bg-gray-50 dark:bg-[#222222]">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <GlobeAltIcon className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-2" />
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">DNS Provider</h3>
          </div>

          <button
            onClick={() => handleDetectProvider(true)}
            disabled={detecting}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 flex items-center disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${detecting ? 'animate-spin' : ''}`} />
            {detecting ? 'Detecting...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Provider Information */}
      {providerInfo && (
        <div className="p-4">
          <div className={`flex items-center justify-between p-3 border rounded-lg ${getProviderColor(providerInfo.provider)}`}>
            <div className="flex items-center">
              <ProviderIcon provider={providerInfo.provider} className="h-8 w-8 mr-3" />
              <div>
                <div className="flex items-center">
                  <span className="text-sm font-medium">
                    {providerInfo.name || 'Unknown Provider'}
                  </span>
                  {providerInfo.confidence && (
                    <span className="ml-2 text-xs px-2 py-0.5 bg-white dark:bg-[#1a1a1a] rounded-full">
                      {Math.round(providerInfo.confidence * 100)}% confidence
                    </span>
                  )}
                  {isConnected && (
                    <CheckCircleIcon className="h-4 w-4 ml-2 text-green-600" title="Connected" />
                  )}
                </div>

                {providerInfo.nameservers && providerInfo.nameservers.length > 0 && (
                  <div className="text-xs mt-1 text-gray-600 dark:text-gray-400">
                    Nameservers: {providerInfo.nameservers.slice(0, 2).join(', ')}
                    {providerInfo.nameservers.length > 2 && ` (+${providerInfo.nameservers.length - 2} more)`}
                  </div>
                )}

                {providerInfo.lastCheck && (
                  <div className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                    Last checked: {new Date(providerInfo.lastCheck).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* Automation Button */}
            {canShowAutomation && (
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={handleAutomateDNS}
                  disabled={oauthInitiating}
                  className="flex items-center px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-orange-600 hover:to-orange-700 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {oauthInitiating ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Redirecting to Cloudflare…
                    </>
                  ) : (
                    <>
                      <ShieldCheckIcon className="h-5 w-5 mr-2" />
                      {providerInfo.provider === 'cloudflare' ? 'Verify with Cloudflare' : 'Add Automatically'}
                      <ChevronRightIcon className="h-5 w-5 ml-2" />
                    </>
                  )}
                </button>
                {providerInfo.provider === 'cloudflare' && !oauthInitiating && (
                  <button
                    onClick={openTokenModal}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 underline underline-offset-2 transition-colors"
                  >
                    or use an API token instead
                  </button>
                )}
              </div>
            )}

            {/* Connected Status */}
            {isConnected && (
              <button
                onClick={handleViewRecords}
                disabled={loadingRecords}
                className="flex items-center px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm font-medium rounded-lg border border-green-200 dark:border-gray-800 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50"
              >
                {loadingRecords ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full mr-2"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    View DNS Records
                    <ChevronRightIcon className="h-4 w-4 ml-2" />
                  </>
                )}
              </button>
            )}

            {/* Manual Setup Notice */}
            {providerInfo.provider !== 'unknown' && !providerInfo.canAutomate && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                Manual setup required
              </div>
            )}
          </div>

          {/* Cloudflare Token Wizard Modal */}
          {showTokenModal && providerInfo.provider === 'cloudflare' && (
            <div className="mt-4 border border-orange-200 dark:border-orange-800/50 rounded-lg overflow-hidden bg-white dark:bg-[#1a1a1a]">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800/50">
                <div className="flex items-center">
                  <KeyIcon className="h-5 w-5 text-orange-600 dark:text-orange-400 mr-2" />
                  <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300">Connect Cloudflare API</h4>
                </div>
                <div className="flex items-center space-x-3">
                  {/* Step Indicators */}
                  <div className="flex items-center space-x-1.5">
                    {[1, 2, 3].map((step) => (
                      <div
                        key={step}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          step === tokenStep
                            ? 'w-6 bg-orange-500'
                            : step < tokenStep
                            ? 'w-1.5 bg-orange-400'
                            : 'w-1.5 bg-orange-200 dark:bg-orange-800'
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={handleCloseModal}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Step 1: Create Token */}
              {tokenStep === 1 && (
                <div className="p-5">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Create a Cloudflare API token to allow automatic DNS record setup for <span className="font-medium text-gray-800 dark:text-gray-200">{domain?.domainName}</span>.
                  </p>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-xs font-bold">1</span>
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <a
                          href={cloudflareTokenUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 underline"
                        >
                          Open Cloudflare API Tokens
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 ml-1" />
                        </a>
                        <span className="text-gray-500 dark:text-gray-400"> → click "Create Token"</span>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-xs font-bold">2</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Use the <span className="font-medium">"Edit zone DNS"</span> template (click "Use template" next to it)
                      </p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-xs font-bold">3</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Under <span className="font-medium">Zone Resources</span>, select your domain, then click "Continue to summary" → "Create Token"
                      </p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-xs font-bold">4</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Copy the token and come back here
                      </p>
                    </div>
                  </div>

                  {/* Required permissions info */}
                  <div className="mb-5 p-3 bg-gray-50 dark:bg-[#222222] rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Required permissions:</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">Zone : Read</span>
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">DNS : Edit</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1.5">The "Edit zone DNS" template includes both of these automatically.</p>
                  </div>

                  <div className="flex items-center justify-end space-x-3">
                    <button
                      onClick={handleCloseModal}
                      className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setTokenStep(2)}
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
                    >
                      I have my token
                      <ChevronRightIcon className="h-4 w-4 ml-1 inline" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Paste Token */}
              {tokenStep === 2 && (
                <div className="p-5">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Paste your Cloudflare API token below. We'll validate it and automatically create all required DNS records.
                  </p>

                  <div className="mb-4">
                    <div className="relative">
                      <input
                        type="text"
                        value={tokenInput}
                        onChange={(e) => {
                          setTokenInput(e.target.value);
                          setValidationError(null);
                        }}
                        placeholder="Paste your Cloudflare API token here"
                        className={`w-full px-4 py-3 text-sm font-mono border rounded-lg bg-gray-50 dark:bg-[#222222] text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors ${
                          validationError
                            ? 'border-red-300 dark:border-red-700 focus:ring-red-500/30'
                            : 'border-gray-300 dark:border-gray-700 focus:ring-orange-500/30 focus:border-orange-400'
                        }`}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && tokenInput.trim()) {
                            processCloudflareToken();
                          }
                        }}
                      />
                      {tokenInput && (
                        <button
                          onClick={() => { setTokenInput(''); setValidationError(null); }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {validationError && (
                      <div className="flex items-center mt-2 text-sm text-red-600 dark:text-red-400">
                        <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                        {validationError}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => { setTokenStep(1); setValidationError(null); }}
                      className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={processCloudflareToken}
                      disabled={validating || !tokenInput.trim()}
                      className="flex items-center px-5 py-2.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {validating ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Validating & Creating Records...
                        </>
                      ) : (
                        <>
                          <ShieldCheckIcon className="h-4 w-4 mr-2" />
                          Connect & Create Records
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Success */}
              {tokenStep === 3 && (
                <div className="p-5">
                  <div className="flex flex-col items-center text-center py-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
                      <CheckCircleSolidIcon className="h-7 w-7 text-green-600 dark:text-green-400" />
                    </div>
                    <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">
                      Cloudflare Connected!
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {createdRecords.length || 'All'} DNS record{createdRecords.length !== 1 ? 's' : ''} created automatically for <span className="font-medium">{domain?.domainName}</span>
                    </p>
                    {failedRecords.length > 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        {failedRecords.length} record{failedRecords.length !== 1 ? 's' : ''} failed — these may need to be added manually
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Domain verification is in progress — it may take a few minutes to propagate.
                    </p>
                  </div>

                  <div className="flex justify-center mt-3">
                    <button
                      onClick={handleCloseModal}
                      className="px-5 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {providerInfo.provider === 'unknown' && (
            <div className="mt-3 p-3 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg">
              <div className="flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div className="ml-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Provider Not Detected
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    You'll need to manually add DNS records through your domain provider's control panel.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DNS Records Display */}
      {showRecords && isConnected && dnsRecords.length > 0 && (
        <div className="p-4 bg-green-50 dark:bg-green-900/10 border-t border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-green-800 dark:text-green-300">
              Active DNS Records ({dnsRecords.length})
            </h4>
            <button
              onClick={() => setShowRecords(false)}
              className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
            >
              Hide
            </button>
          </div>

          <div className="space-y-2 max-h-60 overflow-y-auto">
            {dnsRecords.map((record, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-white dark:bg-[#1a1a1a] rounded border border-green-200 dark:border-gray-800">
                <div className="flex items-center space-x-3">
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                    {record.type}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    {record.name || '@'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono max-w-xs truncate">
                    {record.content}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {record.ttl && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      TTL: {record.ttl}
                    </span>
                  )}
                  <CheckCircleIcon className="h-4 w-4 text-green-600" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4">
          <div className="flex items-center p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div className="ml-2">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              <button
                onClick={() => handleDetectProvider(true)}
                className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 mt-1"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DnsProviderDetection;
